<template>
  <label
    :class="{
      'flex-row flex': hasLabelOrHint,
      'flex items-center': !hasLabelOrHint,
      'gap-3': hasLabelOrHint && size === 'md',
      'gap-2': hasLabelOrHint && size === 'sm',
      'p-3 -m-3': increasedTriggerArea,
    }"
  >
    <CheckboxRoot
      :checked="checked"
      @update:checked="handleUpdateCheckbox"
      :value="value"
      :name="name"
      :disabled="disabled"
      :asChild="asChild"
      :class="cn(variants({ size, checked, disabled }), props.class)"
    >
      <CheckboxIndicator
        class="bg-utility-brand-600_alt h-full w-full flex items-center justify-center rounded-xs"
        :class="{
          'bg-disabled': disabled,
          'bg-disabled_subtle': disabled,
          '[&>svg]:h-3': size === 'sm',
        }"
      >
        <Icon :name="indicatorIcon" size="md" />
      </CheckboxIndicator>
    </CheckboxRoot>
    <span v-if="hasLabelOrHint" class="flex flex-col">
      <InputLabel
        :label="label"
        :required="required"
        :size="size"
        :class="[{ '!mb-0': !hint }]"
      >
        <slot />
      </InputLabel>
      <InputHint v-if="hint" :hint="hint" :size="size">
        <slot name="hint" />
      </InputHint>
    </span>
  </label>
</template>
<script setup>
import { CheckboxIndicator, CheckboxRoot } from "radix-vue";
import { sizes, variants } from ".";
import { cn } from "@/Utils/cn";
import { useSlots, computed, ref, watch } from "vue";
import { Icon } from "@/SharedComponents/Icon";
import { InputLabel } from "@/SharedComponents/InputLabel";
import { InputHint } from "@/SharedComponents/InputHint";

const emit = defineEmits(["update:checked"]);
const props = defineProps({
  label: { type: String, required: false, default: "" },
  hint: { type: String, required: false, default: "" },
  size: {
    type: null,
    validator: (val) => sizes.includes(val),
    required: false,
    default: "md",
  },
  asChild: { type: Boolean, required: false },
  class: { type: null, required: false },
  checked: { type: [Boolean, String], required: false },
  // pass indeterminate prop to enable 3 state checkbox
  indeterminate: { type: Boolean, required: false },
  disabled: { type: Boolean, required: false },
  decorative: { type: Boolean, required: false },
  required: { type: Boolean, required: false },
  name: { type: String, required: false },
  value: { type: [String, Boolean], default: true },
  increasedTriggerArea: { type: Boolean, required: false },
});

/*
 * The value of the checkbox when it's checked.
 * @type {Boolean|String}
 * default: true
 */
const internalPositiveValue = ref(
  props.value !== undefined ? props.value : true
);
const slots = useSlots();
const hasLabelOrHint = computed(() => {
  return props.label || slots.default || props.hint || slots.hint;
});
/*
 * The checked state of the checkbox.
 * @type {Boolean|String}
 * usage v-model:checked
 */
const checked = ref(props.checked);
watch(
  () => props.checked,
  (value) => {
    checked.value = value;
  }
);

const handleUpdateCheckbox = (value) => {
  if (props.decorative) {
    return;
  }

  if (props.indeterminate) {
    if (checked.value === internalPositiveValue.value) checked.value = false;
    else if (checked.value === false) checked.value = "indeterminate";
    else checked.value = internalPositiveValue.value;
  } else {
    checked.value = value ? internalPositiveValue.value : false;
  }
  emit("update:checked", checked.value);
};

const indicatorIcon = computed(() => {
  return checked.value === internalPositiveValue.value
    ? "checkmark-default"
    : checked.value === "indeterminate"
    ? "minus"
    : "";
});
</script>
